import { apiFetch, v1Path } from './api';
import type { ProjectDto, StateDto, WorkItemFull } from './work-types';

/** `text_long` renders as a textarea; `date` expects an ISO `yyyy-mm-dd` string. */
export type ServiceRequestFieldType = 'string' | 'text_long' | 'date' | 'boolean' | 'positive_integer';

export type ServiceRequestFieldSchema = {
  sections?: Record<string, string[]>;
  /** Field key → server-configured choices. The server remains authoritative for accepted values. */
  options?: Record<string, string[]>;
  /** Field key → server-configured scalar control type. */
  field_types?: Record<string, ServiceRequestFieldType>;
  /** Admin-configured display label per field, from the Form builder (Configurable Setup PRD §5.3). */
  field_labels?: Record<string, string>;
  /** Admin-configured help text per field. */
  field_help?: Record<string, string>;
  /** Admin-configured display label per section. */
  section_labels?: Record<string, string>;
  /** Field key → the answer that makes it appear and become required. */
  conditional_required?: Record<string, { field: string; equals: string | boolean }>;
  /** Fields that only count as answered once ticked (sign-offs). */
  must_confirm?: string[];
  /** Section key → ordered items; built-in request fields are prefixed `detail:`. */
  section_layout?: Record<string, string[]>;
  /** Section keys in display order (JSON object key order is not preserved in storage). */
  section_order?: string[];
  /** Questions that also offer "Not yet agreed" and "TBC". */
  allow_tbc?: string[];
  /** The question type chosen in settings, kept so the editor round-trips. */
  question_kinds?: Record<string, string>;
  /** Choice questions that also accept a typed "Other" answer. */
  allow_other?: string[];
  /** Choice questions answered with a list of choices. */
  multi_select?: string[];
  /** Field key → choice → status colour, so an answer reads as red / amber / green. */
  option_colors?: Record<string, Record<string, ServiceRequestOptionColor>>;
  /** Choice questions answered as rows of choice + quantity, e.g. machine models with a count each. */
  quantity_list?: string[];
  /** Quantity list key → the question that holds the total of its quantities. */
  quantity_total?: Record<string, string>;
  /** Section key → the departments that receive a task for that section when the request is pushed. */
  section_routes?: Record<string, {
    project_ids: string[];
    when?: { field: string; equals: string };
    note?: string;
  }>;
};

export type ServiceRequestOptionColor = 'red' | 'amber' | 'green';

export type ServiceRequestQuantityRow = { option: string; quantity: number | null };

/** Built-in request fields placed in a configured section are stored as `detail:<field>`. */
export const DETAIL_ITEM_PREFIX = 'detail:';

export type ConfiguredSection = { key: string; label: string; items: string[] };

/** Section keys in configured order; any key missing from `section_order` follows at the end. */
export function orderedSectionKeys(schema: ServiceRequestFieldSchema | null | undefined): string[] {
  const keys = Object.keys(schema?.sections ?? {});
  const order = (schema?.section_order ?? []).filter((key) => keys.includes(key));
  return [...order, ...keys.filter((key) => !order.includes(key))];
}

/** Sections built in settings, in order. Empty for types that predate the editor. */
export function configuredSections(type: ServiceRequestType | null | undefined): ConfiguredSection[] {
  const schema = type?.field_schema;
  if (!schema?.section_layout) return [];
  return orderedSectionKeys(schema).map((key) => ({
    key,
    label: schema.section_labels?.[key] ?? serviceRequestFieldLabel(key),
    items: schema.section_layout?.[key] ?? schema.sections?.[key] ?? [],
  }));
}

/** A conditional field shows only while its trigger answer matches. */
export function serviceRequestFieldVisible(
  type: ServiceRequestType | null | undefined,
  field: string,
  typeData: Record<string, unknown>,
): boolean {
  const condition = type?.field_schema?.conditional_required?.[field];
  return !condition || typeData[condition.field] === condition.equals;
}

export type ReadinessTemplateItem = { key: string; label: string; required?: boolean };

export type SubmissionRequirements = {
  fields: string[];
  type_data_fields: string[];
  require_attachments: boolean;
};

export type ServiceRequestRuleWhen = 'submit_review' | 'deployment_approval';
export type ServiceRequestRuleOperator = 'equals' | 'not_equals' | 'is_answered' | 'is_not_answered';

/** A declarative `when / check / condition / block-with-message` rule (Configurable Setup PRD §5.4). */
export type ServiceRequestRule = {
  id: string;
  when: ServiceRequestRuleWhen;
  field: string;
  operator: ServiceRequestRuleOperator;
  value: string | number | boolean | null;
  message: string;
};

export type ServiceRequestTaskTemplate = {
  title: string;
  owner_role: string | null;
  due_offset_days: number | null;
};

export type ServiceRequestHandoffConfig = {
  linked_project_id: string;
  /** Column in that stream that means this department's part is done. */
  ready_state_id?: string | null;
  task_templates?: ServiceRequestTaskTemplate[];
};

export type ServiceRequestStageConfigEntry = {
  label?: string;
  link_stream?: boolean;
  /** Single-stream fields from before departments; read as one handoff. */
  linked_project_id?: string | null;
  ready_state_id?: string | null;
  task_templates?: ServiceRequestTaskTemplate[];
  handoffs?: ServiceRequestHandoffConfig[];
};

/** Departments an approved request is handed to, in order. */
export function serviceRequestHandoffs(type: Pick<ServiceRequestType, 'stage_config'> | null | undefined): ServiceRequestHandoffConfig[] {
  const entry = type?.stage_config?.approved ?? type?.stage_config?.in_progress;
  if (!entry?.link_stream) return [];
  if (Array.isArray(entry.handoffs)) return entry.handoffs;
  return entry.linked_project_id
    ? [{ linked_project_id: entry.linked_project_id, ready_state_id: entry.ready_state_id ?? null, task_templates: entry.task_templates ?? [] }]
    : [];
}

export type ServiceRequestVisibility =
  | { mode: 'all' }
  | { mode: 'departments'; departments: string[] };

export type ServiceRequestType = {
  id: string;
  key: string;
  name: string;
  description: string;
  field_schema: ServiceRequestFieldSchema;
  initial_approval_roles: string[];
  deployment_approval_roles: string[];
  submission_requirements?: SubmissionRequirements;
  readiness_template: ReadinessTemplateItem[];
  validation_template?: ReadinessTemplateItem[];
  sla_days: number | null;
  archived_at: string | null;
  required_attachments?: string[];
  required_attachment_guidance?: Record<string, string>;
  department?: string | null;
  visibility?: ServiceRequestVisibility;
  status?: 'draft' | 'published';
  config_version?: number;
  rules?: ServiceRequestRule[];
  stage_config?: Record<string, ServiceRequestStageConfigEntry>;
};

export type ServiceRequestDetails = Record<string, unknown> & {
  request_version: number;
  request_type_id: string;
  requestor_id?: string | null;
  account_owner_id?: string | null;
  commercial_owner_id?: string | null;
  implementation_id?: string | null;
  submitted_at?: string | null;
  last_submitted_at?: string | null;
  completed_by_id?: string | null;
  completed_at?: string | null;
};

export type ServiceRequestApproval = {
  id: string;
  phase: 'initial' | 'deployment';
  role: string;
  decision: 'approved' | 'changes_requested' | 'rejected' | 'commented';
  approver_id?: string | null;
  comments: string | null;
  request_version: number;
  decided_at: string;
  override: boolean;
  override_reason: string | null;
  outstanding_requirement: string | null;
  /** Section key → reviewer note, sent with Request changes. */
  section_feedback?: Record<string, string> | null;
};

export type ServiceRequestReadinessItem = {
  id: string;
  key: string;
  label: string;
  required: boolean;
  completed: boolean;
  evidence: string | null;
  completed_at: string | null;
  completed_by_id?: string | null;
};

export type PersonRef = { user_id: string; name: string; email: string | null };

export type SlaDto = {
  requested_at: string;
  sla_date: string | null;
  target_deployment_date: string | null;
  days_remaining: number | null;
  overdue: boolean;
  at_risk: boolean;
  target_days_remaining: number | null;
  target_overdue: boolean;
};

export type ApproverDto = { role: string; user_id: string; decided_at: string };

export type InitialApprovalStatus = 'pending' | 'approved' | 'changes_required' | 'rejected';

export type InitialApprovalDto = {
  status: InitialApprovalStatus;
  approved_at: string | null;
  approvers: ApproverDto[];
  missing_roles: string[];
  current_role?: string | null;
  decisions: Array<{ role: string; decision: string; user_id: string; decided_at: string }>;
};

export type DeploymentApprovalStatus = 'not_requested' | 'pending' | 'approved' | 'overridden';

export type DeploymentOverride = {
  user_id: string;
  decided_at: string;
  reason: string | null;
  outstanding_requirement: string | null;
};

export type DeploymentApprovalDto = {
  status: DeploymentApprovalStatus;
  approved_at: string | null;
  approvers: ApproverDto[];
  missing_roles: string[];
  current_role?: string | null;
  override: DeploymentOverride | null;
};

export type BlockerCode =
  | 'readiness_incomplete'
  | 'po_missing'
  | 'initial_approval_missing'
  | 'initial_approval_rejected'
  | 'changes_required'
  | 'deployment_approval_missing'
  | 'dependency_incomplete'
  | 'sla_overdue';

export type Blocker = { code: BlockerCode; message: string; hard: boolean };

export type DependencyRef = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  state_name: string | null;
  release_status: string | null;
  completed: boolean;
};

export type SlimRef = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  state_id: string | null;
  state_name: string | null;
  state_group: string | null;
  due_date: string | null;
};

export type Rag = 'green' | 'at_risk' | 'blocked';

export type ServiceRequestStage =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'changes_required'
  | 'approved'
  | 'in_progress'
  | 'ready_for_deployment'
  | 'deployment_approval'
  | 'scheduled'
  | 'deployed'
  | 'validation'
  | 'completed'
  | 'rejected'
  | 'cancelled';

export type ServiceRequestGenerated = {
  site_survey_task_id: string | null;
  development_item_id: string | null;
  site_survey_task: SlimRef | null;
  development_item: SlimRef | null;
};

export type ServiceRequestPermissions = {
  approval_roles: { initial: string[]; deployment: string[] };
  can_override: boolean;
  can_edit: boolean;
  is_org_admin: boolean;
  can_edit_readiness?: boolean;
  can_edit_validation?: boolean;
  can_edit_requestor?: boolean;
  can_delete?: boolean;
};

/** Roles that must approve each phase; deployment includes `Finance` when the conditional rule applies (contract §7). */
export type EffectiveRoles = { initial: string[]; deployment: string[] };

export type ApprovalDecisionResponse = ServiceRequestApproval & {
  phase_complete: boolean;
  transitioned_to: ServiceRequestStage | null;
  state_id: string | null;
};

export type ServiceRequestReadiness = {
  items: ServiceRequestReadinessItem[];
  completed: number;
  required: number;
  percent: number;
  missing: string[];
};

export type ServiceRequestSubmission = {
  total: number;
  completed: number;
  percent: number;
  missing: string[];
};

export type ServiceRequestEscalation = {
  id: string;
  actor_id: string;
  request_version: number;
  reason: string;
  created_at: string;
};

export type ServiceRequest = {
  work_item: WorkItemFull;
  details: ServiceRequestDetails;
  request_type: ServiceRequestType;
  implementation: { id: string; name: string; description: string } | null;
  approvals: ServiceRequestApproval[];
  escalations?: ServiceRequestEscalation[];
  submission: ServiceRequestSubmission;
  readiness: ServiceRequestReadiness;
  validation: ServiceRequestReadiness;
  stage: ServiceRequestStage;
  state_name: string;
  owner: PersonRef | null;
  assignees: PersonRef[];
  requestor: PersonRef | null;
  account_owner: PersonRef | null;
  commercial_owner: PersonRef | null;
  initial_approval: InitialApprovalDto;
  deployment_approval: DeploymentApprovalDto;
  sla: SlaDto;
  dependencies: DependencyRef[];
  blockers: Blocker[];
  is_blocked: boolean;
  rag: Rag;
  rag_reasons: string[];
  generated: ServiceRequestGenerated;
  permissions: ServiceRequestPermissions;
  effective_roles?: EffectiveRoles;
};

/** One approval role and the people named to decide it. */
export type ServiceRequestApprovalRole = {
  role: string;
  approver_ids: string[];
};
export type ServiceRequestRolesResponse = { items: ServiceRequestApprovalRole[]; known_roles: string[] };

/** Contract §6 — one row of the configuration change log. */
export type ServiceRequestConfigAuditRow = {
  id: string;
  entity: 'type' | 'roles' | 'settings' | string;
  entity_id: string | null;
  entity_name: string | null;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  actor: { id: string; name: string } | null;
  created_at: string;
};

/** Error codes the SR UI turns into readable toasts. */
export const SERVICE_REQUEST_ERROR_MESSAGES: Record<string, string> = {
  SERVICE_REQUEST_STAGE_INVALID: 'This decision is not available at the current stage. Refresh to see the latest status.',
  SERVICE_REQUEST_MATERIAL_WITH_TRANSITION: 'Save changes first, then change the status.',
  SERVICE_REQUEST_DEVELOPMENT_PROJECT_MISSING: 'Choose a development backlog project in Service Request settings.',
  SERVICE_REQUEST_GENERATED_RELATION: 'Only an org admin can remove the generated development dependency while custom software is required.',
  SERVICE_REQUEST_PARENT_DRAFT: 'Subtasks cannot start while the Service Request is still a draft.',
};

export function serviceRequestErrorMessage(code: string | null | undefined, fallback: string): string {
  if (code && code in SERVICE_REQUEST_ERROR_MESSAGES) return SERVICE_REQUEST_ERROR_MESSAGES[code];
  return fallback;
}

export const SERVICE_REQUEST_STAGES: ReadonlyArray<{ key: ServiceRequestStage; label: string }> = [
  { key: 'draft', label: 'Draft' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'Under review' },
  { key: 'changes_required', label: 'Changes required' },
  { key: 'approved', label: 'Approved' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'ready_for_deployment', label: 'Ready for deployment' },
  { key: 'deployment_approval', label: 'Deployment approval' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'deployed', label: 'Deployed' },
  { key: 'validation', label: 'Validation' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
];

export function stageLabel(stage: string | null | undefined): string {
  return SERVICE_REQUEST_STAGES.find((row) => row.key === stage)?.label ?? serviceRequestFieldLabel(stage ?? 'unknown');
}

/**
 * The fourteen stages are too many to read at a glance, so the progress bar groups them into the
 * five phases of the lifecycle. Display only — the workflow itself is still stage by stage.
 */
export const SERVICE_REQUEST_PHASES: ReadonlyArray<{
  key: string;
  label: string;
  stages: readonly ServiceRequestStage[];
}> = [
  { key: 'request', label: 'Request', stages: ['draft', 'submitted', 'changes_required'] },
  { key: 'approval', label: 'Approval', stages: ['under_review', 'approved'] },
  { key: 'delivery', label: 'Delivery', stages: ['in_progress', 'ready_for_deployment'] },
  { key: 'deployment', label: 'Deployment', stages: ['deployment_approval', 'scheduled', 'deployed'] },
  { key: 'validation', label: 'Validation', stages: ['validation', 'completed'] },
];

/** Index into `SERVICE_REQUEST_PHASES`, or -1 for the terminal stages that leave the lifecycle. */
export function serviceRequestPhaseIndex(stage: string | null | undefined): number {
  return SERVICE_REQUEST_PHASES.findIndex((phase) => (phase.stages as readonly string[]).includes(stage ?? ''));
}

export type StepState = 'done' | 'current' | 'todo' | 'stopped';

/** Per-phase step states for the progress bar, including the hint shown under the active phase. */
export function serviceRequestPhaseSteps(
  stage: string | null | undefined,
  stateName?: string | null,
): Array<{ key: string; label: string; hint: string | null; state: StepState }> {
  const stopped = stage === 'rejected' || stage === 'cancelled';
  const completed = stage === 'completed';
  const current = serviceRequestPhaseIndex(stage);
  const active = stopped ? 0 : completed ? SERVICE_REQUEST_PHASES.length - 1 : current;
  return SERVICE_REQUEST_PHASES.map((phase, index) => {
    let state: StepState = 'todo';
    if (stopped) state = index === 0 ? 'stopped' : 'todo';
    else if (completed || index < current) state = 'done';
    else if (index === current) state = 'current';
    return {
      key: phase.key,
      label: phase.label,
      hint: index === active ? (stateName?.trim() || stageLabel(stage)) : null,
      state,
    };
  });
}

/** PRD §20 priority names; mirrors `SERVICE_REQUEST_PRIORITY_LABELS` on the server. The SR UI never offers `none`. */
export const PRIORITY_LABELS: Record<'urgent' | 'high' | 'medium' | 'low' | 'none', string> = {
  urgent: 'Critical',
  high: 'High',
  medium: 'Normal',
  low: 'Low',
  none: 'Unset',
};

export const SERVICE_REQUEST_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;

/** Mirrors `SERVICE_REQUEST_ALLOWED_TRANSITIONS` in `services/service-request-definitions.ts`. */
export const SERVICE_REQUEST_ALLOWED_TRANSITIONS: Record<ServiceRequestStage, ServiceRequestStage[]> = {
  draft: ['submitted', 'cancelled'],
  submitted: ['under_review', 'changes_required', 'rejected', 'cancelled'],
  under_review: ['changes_required', 'approved', 'rejected', 'cancelled'],
  changes_required: ['submitted', 'cancelled'],
  approved: ['in_progress', 'cancelled'],
  in_progress: ['ready_for_deployment', 'cancelled'],
  ready_for_deployment: ['deployment_approval', 'in_progress', 'cancelled'],
  deployment_approval: ['scheduled', 'ready_for_deployment', 'cancelled'],
  scheduled: ['deployed', 'cancelled'],
  deployed: ['validation'],
  validation: ['completed', 'in_progress'],
  completed: [],
  rejected: [],
  cancelled: [],
};

export const SERVICE_REQUEST_CLOSED_STAGES: readonly ServiceRequestStage[] = ['completed', 'rejected', 'cancelled'];

export function isServiceRequestStage(value: string | null | undefined): value is ServiceRequestStage {
  return Boolean(value) && SERVICE_REQUEST_STAGES.some((row) => row.key === value);
}

/** Stages reachable from `stage` in one transition; empty for closed or unknown stages. */
export function allowedNextStages(stage: string | null | undefined): ServiceRequestStage[] {
  if (!isServiceRequestStage(stage)) return [];
  return SERVICE_REQUEST_ALLOWED_TRANSITIONS[stage];
}

/**
 * Workflow states offered in the Status dropdown: the current state plus the
 * states mapped to an allowed next stage. States without a stage mapping are
 * hidden so the list never offers a move the gate would reject.
 */
export function transitionStateOptions<T extends { id: string; service_request_stage?: string | null }>(
  states: readonly T[],
  currentStateId: string,
  stage: string | null | undefined,
): T[] {
  const next = new Set<string>(allowedNextStages(stage));
  return states.filter((state) => state.id === currentStateId || (state.service_request_stage != null && next.has(state.service_request_stage)));
}

export function ragLabel(rag: Rag | string | null | undefined): string {
  if (rag === 'blocked') return 'Blocked';
  if (rag === 'at_risk') return 'At risk';
  return 'Green';
}

export function ragClass(rag: Rag | string | null | undefined): string {
  if (rag === 'blocked') return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300';
  if (rag === 'at_risk') return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300';
  return 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-300';
}

function pluralDays(count: number): string {
  return `${count} ${count === 1 ? 'day' : 'days'}`;
}

/** "12 days remaining" / "Overdue by 3 days" / "Due today" / "No SLA". */
export function slaSummary(sla: Pick<SlaDto, 'sla_date' | 'days_remaining' | 'overdue'> | null | undefined): string {
  if (!sla || !sla.sla_date || sla.days_remaining == null) return 'No SLA';
  if (sla.overdue || sla.days_remaining < 0) return `Overdue by ${pluralDays(Math.abs(sla.days_remaining))}`;
  if (sla.days_remaining === 0) return 'Due today';
  return `${pluralDays(sla.days_remaining)} remaining`;
}

export function slaClass(sla: Pick<SlaDto, 'overdue' | 'at_risk'> | null | undefined): string {
  if (sla?.overdue) return ragClass('blocked');
  if (sla?.at_risk) return ragClass('at_risk');
  return '';
}

export function deploymentApprovalLabel(status: DeploymentApprovalStatus | string): string {
  if (status === 'approved') return 'Approved for deployment';
  if (status === 'overridden') return 'Approved (management override)';
  if (status === 'pending') return 'Deployment approval pending';
  return 'Deployment approval not requested';
}

/** Primary workflow action offered for each stage (§5–§15). */
export type StageAction = {
  label: string;
  target: ServiceRequestStage;
  requiresDeploymentApproval?: boolean;
  /** Fallback when the initial-approval auto-transition did not run (contract §12). */
  requiresInitialApproval?: boolean;
  confirmsValidation?: boolean;
  /** Submission gate: blocked while mandatory fields are missing or the draft is dirty. */
  submits?: boolean;
};

export const STAGE_ACTIONS: Partial<Record<ServiceRequestStage, StageAction>> = {
  draft: { label: 'Submit request', target: 'submitted', submits: true },
  changes_required: { label: 'Resubmit request', target: 'submitted', submits: true },
  submitted: { label: 'Start review', target: 'under_review' },
  under_review: { label: 'Mark approved', target: 'approved', requiresInitialApproval: true },
  approved: { label: 'Start work', target: 'in_progress' },
  in_progress: { label: 'Mark ready for deployment', target: 'ready_for_deployment' },
  ready_for_deployment: { label: 'Request deployment approval', target: 'deployment_approval' },
  deployment_approval: { label: 'Schedule deployment', target: 'scheduled', requiresDeploymentApproval: true },
  scheduled: { label: 'Mark deployed', target: 'deployed' },
  deployed: { label: 'Start validation', target: 'validation' },
  validation: { label: 'Confirm validation & complete', target: 'completed', confirmsValidation: true },
};

export function stageAction(stage: ServiceRequestStage | string | null | undefined): StageAction | null {
  if (!stage) return null;
  return STAGE_ACTIONS[stage as ServiceRequestStage] ?? null;
}

export function isDeploymentApproved(approval: Pick<DeploymentApprovalDto, 'status'> | null | undefined): boolean {
  return approval?.status === 'approved' || approval?.status === 'overridden';
}

export const SUBMISSION_COMMON_FIELD_OPTIONS = [
  'title',
  'description',
  'customer',
  'venue_site',
  'opportunity_reference',
  'project_reference',
  'requestor_id',
  'account_owner_id',
  'commercial_owner_id',
  'responsible_department',
  'required_completion_date',
  'target_go_live_date',
  'business_reason',
] as const;

/** Fields the client sends on PATCH /details, keyed by what the draft holds. */
export function detailsPatchBody(
  draft: Record<string, unknown>,
  options: { includeRequestor: boolean },
): Record<string, unknown> {
  const editable: string[] = [
    ...COMMON_SERVICE_REQUEST_FIELDS,
    ...DEPLOYMENT_SERVICE_REQUEST_FIELDS,
    'implementation_id',
    'account_owner_id',
    'commercial_owner_id',
    ...(options.includeRequestor ? ['requestor_id'] : []),
    'type_data',
    'extra_steps',
  ];
  return Object.fromEntries(editable.filter((field) => field in draft).map((field) => [field, draft[field]]));
}

/** Stable comparison of two detail drafts (key order independent). */
export function detailsDirty(saved: Record<string, unknown>, draft: Record<string, unknown>): boolean {
  return stableJson(saved) !== stableJson(draft);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

export function typeDataOf(details: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const raw = details?.type_data;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
}

/* ------------------------- Activity (§6/§12) -------------------------- */

export type ServiceRequestActivityRow = {
  verb: string;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
};

export type ServiceRequestActivityContext = {
  /** Readiness item key → label, from `readiness.items`. */
  readinessLabels?: Record<string, string>;
  /** Validation item key → label, from `validation.items`. */
  validationLabels?: Record<string, string>;
  /** The request's type, so answers read with the question wording configured in settings. */
  requestType?: ServiceRequestType | null;
};

const SERVICE_REQUEST_FIELD_PREFIX = 'service_request.';
const SERVICE_REQUEST_TYPE_DATA_PREFIX = 'service_request.type_data.';
const SERVICE_REQUEST_SECTION_FEEDBACK_PREFIX = 'service_request.section_feedback.';
const SERVICE_REQUEST_READINESS_PREFIX = 'service_request_readiness.';
const SERVICE_REQUEST_READINESS_EVIDENCE_PREFIX = 'service_request_readiness_evidence.';
const SERVICE_REQUEST_VALIDATION_PREFIX = 'service_request_validation.';
const SERVICE_REQUEST_VALIDATION_EVIDENCE_PREFIX = 'service_request_validation_evidence.';

function activityValue(key: string, raw: string | null, type?: ServiceRequestType | null): string {
  if (raw == null || raw === '') return '—';
  const kind = serviceRequestFieldKind(key, type);
  if (kind === 'boolean') return raw === 'true' ? 'Yes' : raw === 'false' ? 'No' : raw;
  return raw;
}

function approvalDecisionText(decision: string): string {
  if (decision === 'approved') return 'approved';
  if (decision === 'changes_requested') return 'requested changes';
  if (decision === 'rejected') return 'rejected';
  if (decision === 'commented') return 'commented';
  return decision.replaceAll('_', ' ');
}

function approvalPhaseText(phase: string): string {
  if (phase === 'initial') return 'initial approval';
  if (phase === 'deployment') return 'deployment approval';
  return phase.replaceAll('_', ' ');
}

/**
 * Human line for a Service Request activity row, or null when the row is not
 * SR-specific (the generic formatter then applies).
 */
export function formatServiceRequestActivityLine(
  row: ServiceRequestActivityRow,
  context: ServiceRequestActivityContext = {},
): string | null {
  const field = row.field ?? '';
  if (row.verb === 'approval_decided') {
    const parts = (row.new_value ?? '').split(':');
    if (parts.length >= 4) {
      const [phase, ...rest] = parts;
      rest.pop();
      const decision = rest.pop() ?? '';
      const role = rest.join(':');
      return `${role} ${approvalDecisionText(decision)} (${approvalPhaseText(phase)})`;
    }
    return 'Recorded an approval decision';
  }
  if (row.verb === 'deployment_overridden') {
    const reason = row.new_value ? ` — reason: ${row.new_value}` : '';
    const outstanding = row.old_value ? ` (outstanding: ${row.old_value})` : '';
    return `Deployment approved by management override${reason}${outstanding}`;
  }
  if (row.verb === 'escalated') {
    return row.new_value ? `Escalated — ${row.new_value}` : 'Escalated';
  }
  if (field.startsWith(SERVICE_REQUEST_VALIDATION_EVIDENCE_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_VALIDATION_EVIDENCE_PREFIX.length);
    const label = context.validationLabels?.[key] ?? serviceRequestFieldLabel(key);
    return row.new_value ? `Validation — ${label}: evidence set to ${row.new_value}` : `Validation — ${label}: evidence cleared`;
  }
  if (field.startsWith(SERVICE_REQUEST_VALIDATION_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_VALIDATION_PREFIX.length);
    const label = context.validationLabels?.[key] ?? serviceRequestFieldLabel(key);
    return `Validation — ${label}: ${row.new_value === 'complete' ? 'complete' : 'incomplete'}`;
  }
  if (field.startsWith(SERVICE_REQUEST_READINESS_EVIDENCE_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_READINESS_EVIDENCE_PREFIX.length);
    const label = context.readinessLabels?.[key] ?? serviceRequestFieldLabel(key);
    return row.new_value ? `Readiness — ${label}: evidence set to ${row.new_value}` : `Readiness — ${label}: evidence cleared`;
  }
  if (field.startsWith(SERVICE_REQUEST_READINESS_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_READINESS_PREFIX.length);
    const label = context.readinessLabels?.[key] ?? serviceRequestFieldLabel(key);
    return `Readiness — ${label}: ${row.new_value === 'complete' ? 'complete' : 'incomplete'}`;
  }
  if (field.startsWith(SERVICE_REQUEST_SECTION_FEEDBACK_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_SECTION_FEEDBACK_PREFIX.length);
    const label = context.requestType?.field_schema?.section_labels?.[key] ?? serviceRequestFieldLabel(key);
    return `${row.old_value ? `${row.old_value} flagged` : 'Flagged'} ${label}: ${row.new_value ?? ''}`;
  }
  if (field.startsWith(SERVICE_REQUEST_TYPE_DATA_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_TYPE_DATA_PREFIX.length);
    const type = context.requestType;
    return `${serviceRequestFieldLabel(key, type)}: ${activityValue(key, row.old_value, type)} → ${activityValue(key, row.new_value, type)}`;
  }
  if (field.startsWith(SERVICE_REQUEST_FIELD_PREFIX)) {
    const key = field.slice(SERVICE_REQUEST_FIELD_PREFIX.length);
    const label = serviceRequestFieldLabel(key);
    if (key === 'requestor_id' || key === 'account_owner_id' || key === 'commercial_owner_id' || key === 'implementation_id') {
      return row.new_value ? `${label} changed` : `${label} cleared`;
    }
    return `${label}: ${activityValue(key, row.old_value)} → ${activityValue(key, row.new_value)}`;
  }
  return null;
}

/** Readiness key → label map for the activity formatter. */
export function readinessLabelMap(items: ReadonlyArray<Pick<ServiceRequestReadinessItem, 'key' | 'label'>>): Record<string, string> {
  return Object.fromEntries(items.map((item) => [item.key, item.label]));
}

/* --------------------------- Approvals (§3) --------------------------- */

export type ApprovalPhase = 'initial' | 'deployment';

/** Which approval phase the current stage accepts decisions for (contract §3), or null outside those stages. */
const REAPPROVAL_STAGES_INITIAL = new Set(['approved', 'in_progress', 'ready_for_deployment', 'deployment_approval', 'scheduled']);

/**
 * Phase whose decisions the server currently accepts. Beyond the review stages a phase
 * re-opens only when a material change has invalidated a previous approval.
 */
export function approvalPhaseForStage(
  stage: string | null | undefined,
  status?: { initial?: string | null; deployment?: string | null },
): ApprovalPhase | null {
  if (stage === 'submitted' || stage === 'under_review') return 'initial';
  if (stage === 'deployment_approval') return 'deployment';
  if (stage && REAPPROVAL_STAGES_INITIAL.has(stage) && status?.initial && status.initial !== 'approved') return 'initial';
  if (stage === 'scheduled' && status?.deployment && status.deployment !== 'approved' && status.deployment !== 'overridden') return 'deployment';
  return null;
}

export type ApprovalRoleRow = {
  role: string;
  /** Latest non-comment decision for the role at the current version, if any. */
  decision: ServiceRequestApproval | null;
  /** Viewer may record a decision for this role. */
  actionable: boolean;
  state: 'approved' | 'current' | 'waiting';
};

/** One row per effective role for the phase, with the viewer's authority and the latest decision at `currentVersion`. */
export function approvalRoleRows(
  request: Pick<ServiceRequest, 'approvals' | 'request_type' | 'permissions' | 'effective_roles'>,
  phase: ApprovalPhase,
  currentVersion: number,
  currentRole?: string | null,
): ApprovalRoleRow[] {
  const effective = request.effective_roles?.[phase]
    ?? (phase === 'initial' ? request.request_type.initial_approval_roles : request.request_type.deployment_approval_roles);
  const granted = new Set(request.permissions?.approval_roles?.[phase] ?? []);
  const roles = Array.from(new Set(effective));
  const decisions = roles.map((role) => request.approvals
    .filter((row) => row.phase === phase && row.role === role && row.request_version === currentVersion && row.decision !== 'commented')
    .sort((left, right) => right.decided_at.localeCompare(left.decided_at) || right.id.localeCompare(left.id))[0] ?? null);
  const activeRole = currentRole === undefined
    ? roles.find((role, index) => decisions[index]?.decision !== 'approved') ?? null
    : currentRole;
  return roles.map((role, index) => {
    const decision = decisions[index];
    const state = decision?.decision === 'approved' ? 'approved' : role === activeRole ? 'current' : 'waiting';
    return {
      role,
      decision,
      state,
      actionable: state === 'current' && granted.has(role),
    };
  });
}

export const COMMON_SERVICE_REQUEST_FIELDS = [
  'customer',
  'venue_site',
  'opportunity_reference',
  'project_reference',
  'responsible_department',
  'required_completion_date',
  'target_go_live_date',
  'business_reason',
] as const;

export const DEPLOYMENT_SERVICE_REQUEST_FIELDS = [
  'deployment_date',
  'deployment_team',
  'site_contact',
  'logistics_details',
  'installer',
  'device_serial_numbers',
] as const;

export function serviceRequestFieldLabel(key: string, type?: ServiceRequestType | null): string {
  const configured = type?.field_schema?.field_labels?.[key];
  if (configured) return configured;
  const overrides: Record<string, string> = {
    venue_site: 'Venue / site',
    opportunity_reference: 'Opportunity / deal',
    project_reference: 'Project / deployment',
    ui: 'UI',
    skus: 'SKUs',
    phone_email: 'Phone / email',
    requestor_id: 'Requestor',
    account_owner_id: 'Sales owner / account owner',
    commercial_owner_id: 'Commercial owner',
    implementation_id: 'Implementation',
    submitted_at: 'Submitted at',
    completed_at: 'Completed at',
    completed_by_id: 'Completed by',
    deployment_date: 'Deployment date',
    deployment_team: 'Deployment team',
    site_contact: 'Site contact',
    logistics_details: 'Logistics details',
    device_serial_numbers: 'Device serial numbers',
    customer_sales: 'Customer & Sales Details',
    deal_project_status: 'Deal / Project Status',
    deployment_overview: 'Deployment Overview',
    machine_product_configuration: 'Machine & Product Configuration',
    branding_ui_development: 'Branding, UI & Development',
    company_type: 'Company type',
    main_customer_contact_name: 'Main customer contact name',
    customer_email: 'Email address',
    customer_phone: 'Telephone number',
    deal_status: 'Deal status',
    contract_discussed: 'Contract discussed',
    contract_status: 'Contract agreed / current contract status recorded',
    payment_terms_agreed: 'Payment terms agreed',
    commercial_ownership_model: 'Commercial ownership model',
    country_region: 'Country / region',
    location_scope: 'Single or multiple locations',
    approximate_location_count: 'Approximate number of locations',
    locations_status: 'Known locations listed or TBC',
    device_types: 'Machine model / type agreed',
    number_of_devices: 'Number of machines agreed',
    deployment_type: 'Deployment type',
    shelf_configuration_status: 'Shelf configuration discussed / agreed',
    product_type: 'Product type identified',
    planogram_discussed: 'Planogram discussed',
    planogram_confirmed: 'Planogram confirmed',
    product_layout_type: 'Standard or non-standard product layout identified',
    branding_type: 'Branding',
    ui_version_configuration: 'UI version / type confirmed where applicable',
    bespoke_requirements_identified: 'Any bespoke UI, software or machine requirements identified',
    design_studio_work_identified: 'Any additional Design Studio work identified',
    connectivity_integration_requirement: 'Connectivity / integration requirement',
    payment_platform_section: 'Payment Platform',
    payment_platform: 'Payment platform identified',
    non_standard_payment_requirements: 'Any non-standard payment requirements recorded',
    installation_timing: 'Installation & Timing',
    installation_target_date: 'Target installation date discussed',
    approximate_installation_timeline: 'Approximate installation timeline, if no firm date',
    critical_deadline_reason: 'Critical deadline / reason for date',
    site_inspection_completed: 'Site inspection completed',
    machine_location_agreed: 'Machine location agreed',
    installation_considerations: 'Known installation considerations',
    refill_operation: 'Refill / Ongoing Operation',
    refill_responsibility_agreed: 'Refill responsibility agreed',
    refill_model: 'Refill model',
    non_standard_requirements: 'Non-Standard Requirements & Commitments',
    additional_development_requirements: 'Additional development requirements',
    bespoke_software_requirements: 'Bespoke software requirements',
    design_studio_requirements: 'Design Studio requirements',
    customer_specific_commitments: 'Customer-specific commitments or promises',
    other_requirements_discussed: 'Other requirements discussed during the sales process',
    documents_confirmation: 'Documents & Confirmation',
    quote_proposal_reference: 'Quote / proposal attached or linked',
    contract_document_reference: 'Contract attached or linked',
    site_photos_reference: 'Relevant site photos / drawings attached or linked, if available',
    branding_brief_reference: 'Branding brief / artwork attached or linked, if available',
    supporting_correspondence_reference: 'Relevant supporting correspondence attached or linked',
    sales_confirmation_section: 'Sales Confirmation',
    exception_to_standard_offering: 'Anything agreed outside the standard offering?',
    exception_details: 'Standard offering exception details',
    sales_confirmation: 'Sales confirms the information above is accurate',
  };
  return overrides[key] ?? key.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function serviceRequestSchemaFieldType(
  type: ServiceRequestType | null | undefined,
  key: string,
): ServiceRequestFieldType | null {
  const value = type?.field_schema?.field_types?.[key];
  return value === 'string' || value === 'text_long' || value === 'date' || value === 'boolean' || value === 'positive_integer'
    ? value
    : null;
}

export function serviceRequestFieldOptions(
  type: ServiceRequestType | null | undefined,
  key: string,
): string[] {
  const values = type?.field_schema?.options?.[key];
  return Array.isArray(values) ? values.filter((value): value is string => typeof value === 'string') : [];
}

export function serviceRequestOptionColor(
  type: ServiceRequestType | null | undefined,
  key: string,
  option: string,
): ServiceRequestOptionColor | null {
  return type?.field_schema?.option_colors?.[key]?.[option] ?? null;
}

export function serviceRequestFieldAllowsOther(type: ServiceRequestType | null | undefined, key: string): boolean {
  return Boolean(type?.field_schema?.allow_other?.includes(key));
}

export function serviceRequestFieldIsMulti(type: ServiceRequestType | null | undefined, key: string): boolean {
  return Boolean(type?.field_schema?.multi_select?.includes(key));
}

export function serviceRequestFieldIsQuantityList(type: ServiceRequestType | null | undefined, key: string): boolean {
  return Boolean(type?.field_schema?.quantity_list?.includes(key));
}

/** The question whose quantities add up into `key`, when `key` is a calculated total. */
export function serviceRequestQuantitySourceOf(type: ServiceRequestType | null | undefined, key: string): string | null {
  const totals = type?.field_schema?.quantity_total ?? {};
  return Object.keys(totals).find((list) => totals[list] === key) ?? null;
}

/** Rows of a quantity list; an answer saved before the question was a list becomes one row. */
export function quantityRowsOf(value: unknown, legacyQuantity?: unknown): ServiceRequestQuantityRow[] {
  if (Array.isArray(value)) {
    return value
      .filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object')
      .map((row) => ({
        option: typeof row.option === 'string' ? row.option : '',
        quantity: typeof row.quantity === 'number' ? row.quantity : null,
      }));
  }
  if (typeof value === 'string' && value.trim()) {
    return [{ option: value.trim(), quantity: typeof legacyQuantity === 'number' && legacyQuantity >= 1 ? legacyQuantity : null }];
  }
  return [];
}

export function quantityRowsTotal(rows: ServiceRequestQuantityRow[]): number {
  return rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0);
}

export function quantityRowsComplete(rows: ServiceRequestQuantityRow[]): boolean {
  return rows.length > 0 && rows.every((row) => row.option.trim().length > 0 && (row.quantity ?? 0) >= 1);
}

export function serviceRequestFieldKind(
  key: string,
  type?: ServiceRequestType | null,
): 'boolean' | 'date' | 'number' | 'textarea' | 'text' {
  const configured = serviceRequestSchemaFieldType(type, key);
  if (configured === 'boolean') return 'boolean';
  if (configured === 'positive_integer') return 'number';
  if (configured === 'date') return 'date';
  if (configured === 'text_long') return 'textarea';
  if (configured === 'string') return 'text';
  if (
    key.endsWith('_required') ||
    key.endsWith('_completed') ||
    key.endsWith('_confirmed') ||
    key === 'contract_signed' ||
    key === 'po_required' ||
    key === 'bright_blue_payments' ||
    key === 'bright_blue_managed_stock' ||
    key === 'retail_media_included' ||
    key === 'custom_software_required' ||
    key === 'creative_approved'
  ) return 'boolean';
  if (key.includes('date')) return 'date';
  if (key === 'number_of_devices') return 'number';
  if (['business_reason', 'logistics_details', 'installation_instructions', 'configuration'].includes(key)) return 'textarea';
  return 'text';
}

export async function listServiceRequestTypes(includeArchived = false, includeDrafts = false): Promise<ServiceRequestType[]> {
  const params = new URLSearchParams();
  if (includeArchived) params.set('include_archived', 'true');
  if (includeDrafts) params.set('include_drafts', 'true');
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const result = await apiFetch<{ items: ServiceRequestType[] }>(
    v1Path(`/service-request-types${suffix}`),
  );
  return result.items;
}

export async function getServiceRequestRoles(): Promise<ServiceRequestRolesResponse> {
  return apiFetch<ServiceRequestRolesResponse>(v1Path('/service-request-roles'));
}

export async function putServiceRequestRoles(items: Array<{ role: string; user_ids: string[] }>): Promise<ServiceRequestRolesResponse> {
  return apiFetch<ServiceRequestRolesResponse>(v1Path('/service-request-roles'), {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

export type ServiceRequestTemplateDto = {
  id: string;
  name: string;
  description: string;
  source_type_id: string | null;
  config: Partial<Pick<ServiceRequestType,
    'description' | 'department' | 'sla_days' | 'field_schema' | 'rules' | 'stage_config'
    | 'initial_approval_roles' | 'deployment_approval_roles' | 'submission_requirements'
    | 'required_attachments' | 'required_attachment_guidance' | 'readiness_template' | 'validation_template'>>;
  created_at: string;
  updated_at: string;
};

export async function listServiceRequestTemplates(): Promise<ServiceRequestTemplateDto[]> {
  return (await apiFetch<{ items: ServiceRequestTemplateDto[] }>(v1Path('/service-request-templates'))).items;
}

/** Saving under an existing template name replaces that template. */
export async function saveServiceRequestTemplate(input: {
  name: string;
  source_type_id: string | null;
  config: Record<string, unknown>;
}): Promise<ServiceRequestTemplateDto> {
  return apiFetch<ServiceRequestTemplateDto>(v1Path('/service-request-templates'), {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteServiceRequestTemplate(id: string): Promise<void> {
  await apiFetch<void>(v1Path(`/service-request-templates/${id}`), { method: 'DELETE' });
}

export async function listServiceRequestConfigAudit(limit = 100): Promise<ServiceRequestConfigAuditRow[]> {
  const result = await apiFetch<{ items: ServiceRequestConfigAuditRow[] }>(
    v1Path(`/service-request-config-audit?limit=${limit}`),
  );
  return result.items;
}

/** Soft delete: the request leaves every list, but the record is kept. */
export async function deleteServiceRequest(item: Pick<WorkItemFull, 'id' | 'project_id'>): Promise<void> {
  await apiFetch<void>(v1Path(`/projects/${item.project_id}/service-requests/${item.id}`), { method: 'DELETE' });
  window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
}

export function serviceRequestPath(item: Pick<WorkItemFull, 'id' | 'project_id'>): string {
  return `/projects/${item.project_id}/service-requests/${item.id}`;
}

export const SERVICE_REQUEST_CREATE_PATH = '/service-requests/new';
export const SERVICE_REQUEST_PROJECT_IDENTIFIER = 'SR';

/**
 * The hidden workspace that stores Service Requests and their workflow states. It is never a
 * stream people pick: requests are a work type, opened from Service Requests.
 */
export function isServiceRequestProject(project: { identifier?: string | null } | null | undefined): boolean {
  return project?.identifier === SERVICE_REQUEST_PROJECT_IDENTIFIER;
}

export type ServiceRequestWorkspace = { project: ProjectDto; states: StateDto[] };

/** The org's Service Request workspace; the server creates it on first use and joins the caller. */
export function fetchServiceRequestWorkspace(): Promise<ServiceRequestWorkspace> {
  return apiFetch<ServiceRequestWorkspace>(v1Path('/service-requests/workspace'), { method: 'POST' });
}
